import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { AlertCircle, ArrowLeft, KeyRound, Lock, Mail, ShieldCheck } from 'lucide-react'
import { api } from '../lib/api'
import Input from '../components/Input'
import LoadingButton from '../components/LoadingButton'

export default function ForgotPasswordPage() {
  const navigate = useNavigate()

  // Step 1: 'email', Step 2: 'otp', Step 3: 'reset'
  const [step, setStep] = useState<'email' | 'otp' | 'reset'>('email')

  // Form Fields
  const [email, setEmail] = useState('')
  const [otp, setOtp] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  // Validation Errors & Server Error
  const [emailError, setEmailError] = useState('')
  const [otpError, setOtpError] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [confirmPasswordError, setConfirmPasswordError] = useState('')
  const [serverError, setServerError] = useState('')

  // Resend OTP Cooldown
  const [cooldown, setCooldown] = useState(0)

  // Loading States
  const [buttonState, setButtonState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')

  // Cooldown countdown timer
  useEffect(() => {
    if (cooldown <= 0) return
    const timer = setInterval(() => {
      setCooldown((prev) => prev - 1)
    }, 1000)
    return () => clearInterval(timer)
  }, [cooldown])

  // Email Validation
  const validateEmail = (val: string): boolean => {
    if (!val.trim()) {
      setEmailError('Email address is required')
      return false
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(val.trim())) {
      setEmailError('Please enter a valid email address')
      return false
    }
    setEmailError('')
    return true
  }

  // OTP Validation
  const validateOtp = (val: string): boolean => {
    if (!val.trim()) {
      setOtpError('Verification code is required')
      return false
    }
    if (!/^\d{6}$/.test(val.trim())) {
      setOtpError('Verification code must be exactly 6 digits')
      return false
    }
    setOtpError('')
    return true
  }

  // Password Validations
  const validateNewPassword = (val: string): boolean => {
    if (!val) {
      setPasswordError('New password is required')
      return false
    }
    if (val.length < 8) {
      setPasswordError('Password must be at least 8 characters long')
      return false
    }
    setPasswordError('')
    return true
  }

  const validateConfirmPassword = (val: string, passVal = newPassword): boolean => {
    if (!val) {
      setConfirmPasswordError('Please confirm your new password')
      return false
    }
    if (val !== passVal) {
      setConfirmPasswordError('Passwords do not match')
      return false
    }
    setConfirmPasswordError('')
    return true
  }

  // Step 1: Send OTP
  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    setServerError('')

    if (!validateEmail(email)) {
      toast.error('Please enter a valid email address')
      return
    }

    setButtonState('loading')
    try {
      await api.forgotPassword(email.trim())
      setButtonState('success')
      toast.success('Reset code sent to your email!')
      setStep('otp')
      setCooldown(60)
      setTimeout(() => setButtonState('idle'), 1000)
    } catch (err: any) {
      console.error('[ForgotPassword] Send OTP error:', err)
      setButtonState('error')
      setTimeout(() => setButtonState('idle'), 2000)
      const msg = err.response?.data?.message || err.message || 'Failed to send reset code'
      setServerError(`❌ ${msg}`)
      toast.error(msg)
    }
  }

  // Step 2: Verify OTP
  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    setServerError('')

    if (!validateOtp(otp)) {
      toast.error('Please enter a valid 6-digit verification code')
      return
    }

    setButtonState('loading')
    try {
      await api.verifyForgotPasswordOtp(email.trim(), otp.trim())
      setButtonState('success')
      toast.success('Code verified successfully!')
      setStep('reset')
      setTimeout(() => setButtonState('idle'), 1000)
    } catch (err: any) {
      console.error('[ForgotPassword] Verify OTP error:', err)
      setButtonState('error')
      setTimeout(() => setButtonState('idle'), 2000)
      const msg = err.response?.data?.message || err.message || 'Invalid or expired verification code'
      setServerError(`❌ ${msg}`)
      toast.error(msg)
    }
  }

  // Step 2: Resend OTP
  const handleResendOtp = async () => {
    if (cooldown > 0) return
    setServerError('')
    try {
      await api.forgotPassword(email.trim())
      setCooldown(60)
      toast.success('A new reset code has been sent!')
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to resend reset code'
      toast.error(msg)
    }
  }

  // Step 3: Reset Password
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setServerError('')

    const isPassValid = validateNewPassword(newPassword)
    const isConfirmValid = validateConfirmPassword(confirmPassword, newPassword)

    if (!isPassValid || !isConfirmValid) {
      toast.error('Please fix the password errors')
      return
    }

    setButtonState('loading')
    try {
      await api.resetPassword(email.trim(), otp.trim(), newPassword)
      setButtonState('success')
      toast.success('Password reset successfully! Redirecting to login...')
      setTimeout(() => {
        navigate('/login')
      }, 1500)
    } catch (err: any) {
      console.error('[ForgotPassword] Reset Password error:', err)
      setButtonState('error')
      setTimeout(() => setButtonState('idle'), 2000)
      const msg = err.response?.data?.message || err.message || 'Failed to reset password'
      setServerError(`❌ ${msg}`)
      toast.error(msg)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-[#F6EEE7] to-gray-100 flex items-center justify-center px-4 py-8">
      {/* Background accent */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#8B4513] rounded-full mix-blend-multiply filter blur-3xl opacity-10" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#6D3A0D] rounded-full mix-blend-multiply filter blur-3xl opacity-10" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border-2 border-[#F6EEE7]">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#6D3A0D] to-[#8B4513] px-8 py-10">
            <div className="flex items-center justify-center mb-3">
              <div className="bg-white/20 p-3 rounded-xl backdrop-blur-sm">
                <KeyRound className="text-white" size={28} />
              </div>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white text-center">Reset Password</h1>
            <p className="text-amber-100 text-center text-sm mt-1">
              {step === 'email' && 'Enter your admin email to receive a reset code'}
              {step === 'otp' && `Enter the 6-digit code sent to ${email}`}
              {step === 'reset' && 'Create a new secure password for your account'}
            </p>
          </div>

          {/* Content */}
          <div className="px-8 py-8">
            {/* Top Error Alert Banner */}
            {serverError && (
              <div className="mb-6 flex items-start gap-3 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-red-800 text-sm animate-fade-in">
                <AlertCircle size={18} className="text-red-600 mt-0.5 flex-shrink-0" />
                <div className="font-medium">{serverError}</div>
              </div>
            )}

            {/* STEP 1: EMAIL */}
            {step === 'email' && (
              <form onSubmit={handleSendOtp} noValidate className="space-y-5">
                <Input
                  type="email"
                  label="Admin Email Address"
                  icon={<Mail size={18} />}
                  value={email}
                  error={emailError}
                  onChange={(e) => {
                    setEmail(e.target.value)
                    if (emailError) validateEmail(e.target.value)
                  }}
                  placeholder="admin@example.com"
                  autoComplete="email"
                  disabled={buttonState === 'loading'}
                  required
                />

                <LoadingButton
                  type="submit"
                  state={buttonState}
                  variant="primary"
                  size="lg"
                  className="w-full mt-6 bg-[#6D3A0D] hover:bg-[#5D2A0D] text-white font-semibold py-3"
                  loadingText="Sending Code..."
                  successText="Code Sent!"
                  errorText="Failed to Send"
                >
                  Send Reset Code
                </LoadingButton>

                <div className="pt-2 text-center">
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#6D3A0D] hover:text-[#5D2A0D] hover:underline"
                  >
                    <ArrowLeft size={16} /> Back to Sign In
                  </Link>
                </div>
              </form>
            )}

            {/* STEP 2: OTP */}
            {step === 'otp' && (
              <form onSubmit={handleVerifyOtp} noValidate className="space-y-5">
                <Input
                  type="text"
                  label="6-Digit Verification Code"
                  icon={<ShieldCheck size={18} />}
                  value={otp}
                  error={otpError}
                  onChange={(e) => {
                    const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 6)
                    setOtp(digitsOnly)
                    if (otpError) validateOtp(digitsOnly)
                  }}
                  placeholder="e.g. 123456"
                  maxLength={6}
                  disabled={buttonState === 'loading'}
                  required
                />

                <div className="flex items-center justify-between text-xs text-gray-500 pt-1">
                  <span>Didn't receive the code?</span>
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={cooldown > 0}
                    className={`font-semibold ${
                      cooldown > 0 ? 'text-gray-400 cursor-not-allowed' : 'text-[#6D3A0D] hover:underline'
                    }`}
                  >
                    {cooldown > 0 ? `Resend Code (${cooldown}s)` : 'Resend Code'}
                  </button>
                </div>

                <LoadingButton
                  type="submit"
                  state={buttonState}
                  variant="primary"
                  size="lg"
                  className="w-full mt-6 bg-[#6D3A0D] hover:bg-[#5D2A0D] text-white font-semibold py-3"
                  loadingText="Verifying Code..."
                  successText="Verified!"
                  errorText="Verification Failed"
                >
                  Verify Code
                </LoadingButton>

                <div className="pt-2 text-center">
                  <button
                    type="button"
                    onClick={() => {
                      setStep('email')
                      setServerError('')
                    }}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-600 hover:text-gray-900 hover:underline"
                  >
                    <ArrowLeft size={16} /> Change Email
                  </button>
                </div>
              </form>
            )}

            {/* STEP 3: RESET PASSWORD */}
            {step === 'reset' && (
              <form onSubmit={handleResetPassword} noValidate className="space-y-5">
                <Input
                  type="password"
                  label="New Password"
                  icon={<Lock size={18} />}
                  value={newPassword}
                  error={passwordError}
                  onChange={(e) => {
                    setNewPassword(e.target.value)
                    if (passwordError) validateNewPassword(e.target.value)
                    if (confirmPassword) validateConfirmPassword(confirmPassword, e.target.value)
                  }}
                  placeholder="Min 8 characters"
                  disabled={buttonState === 'loading'}
                  required
                />

                <Input
                  type="password"
                  label="Confirm New Password"
                  icon={<Lock size={18} />}
                  value={confirmPassword}
                  error={confirmPasswordError}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value)
                    if (confirmPasswordError) validateConfirmPassword(e.target.value, newPassword)
                  }}
                  placeholder="Re-enter new password"
                  disabled={buttonState === 'loading'}
                  required
                />

                <LoadingButton
                  type="submit"
                  state={buttonState}
                  variant="primary"
                  size="lg"
                  className="w-full mt-6 bg-[#6D3A0D] hover:bg-[#5D2A0D] text-white font-semibold py-3"
                  loadingText="Resetting Password..."
                  successText="Password Reset!"
                  errorText="Reset Failed"
                >
                  Reset Password
                </LoadingButton>
              </form>
            )}
          </div>

          {/* Footer */}
          <div className="px-8 py-4 bg-[#F6EEE7]/60 border-t-2 border-[#F6EEE7] text-center">
            <p className="text-xs text-[#6D3A0D] font-medium">
              © 2026 Te Amo E-Commerce Admin Panel
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
